import { useState } from 'react';
import useDropdown from './useDropdown';
import { toLower } from 'lodash';
import { DropdownType } from '../components/ProjectSearchDropdown/types';

export interface SearchDropdownItem<T = unknown> {
  value: any;
  label: string;
  searchLabel: string;
}

export const getSearchDropdownItems = <T>(
  values: readonly T[],
  processLabel: (value: T) => string,
): SearchDropdownItem<T>[] => {
  return values?.map((item) => {
    const label = processLabel(item);
    return {
      value: item,
      label,
      searchLabel: toLower(label),
    };
  }) as SearchDropdownItem<T>[];
};

export const getSearchDropdownItemsFromTitleId = <T extends { title: string; id: string }>(
  values: readonly T[],
): SearchDropdownItem<T>[] => {
  return values?.map((item) => ({
    value: item.id,
    label: item.title,
    searchLabel: toLower(item.title),
  })) as SearchDropdownItem<T>[];
};

export const getTextFromSelectedItems = (items: SearchDropdownItem[]): string | undefined => {
  return items && items?.length > 0
    ? items.reduce((result, item) => result.concat(', ', item.label), '' as string).substring(2)
    : undefined;
};

const useSearchDropdown = ({
  items,
  singleSelect,
  dropdownType,
}: {
  items: SearchDropdownItem[];
  singleSelect?: boolean;
  dropdownType: DropdownType;
}) => {
  const dropdownProps = useDropdown();
  const [selectedItems, setSelectedItems] = useState<SearchDropdownItem[]>([]);

  const onItemClick = (item: SearchDropdownItem) => {
    setSelectedItems((oldData) => {
      const foundItem = oldData?.find((findItem) => findItem.value === item.value);
      if (foundItem) {
        return oldData?.filter((filterItem) => filterItem.value !== item.value);
      } else if (singleSelect) {
        return [item];
      }
      const newData = [...oldData];
      newData?.push(item);
      return newData;
    });
  };

  return { ...dropdownProps, items, selectedItems, dropdownType, setSelectedItems, onItemClick };
};

export default useSearchDropdown;
